/**
 * The wire protocol.
 *
 * Every message crossing the socket is parsed by a schema here before anything
 * else touches it. Not validated after the fact, not trusted because the client
 * is ours — parsed at the boundary, so past that point the types are facts. A
 * poker server's clients are adversarial by definition: some of them are trying
 * to act out of turn, bet negative amounts, or sit twice.
 *
 * Cards on the wire are strings (`"Ks"`), not the engine's integers. A hand
 * history someone opens in a support ticket should be readable, and the cost is
 * one conversion at the edge.
 *
 * The critical asymmetry lives in `ServerEvent`: it is what a *particular seat*
 * is allowed to know. Hole cards belong to exactly one recipient until
 * showdown, so the server builds a different message per seat rather than one
 * message everyone filters. Filtering on the client is not privacy.
 */

import { z } from 'zod';

export const PROTOCOL_VERSION = 1;

/** `"Ks"`, `"2h"` — rank then suit, suit lowercase. */
export const CardCode = z.string().regex(/^[23456789TJQKA][shdc]$/, 'not a card code');
export type CardCode = z.infer<typeof CardCode>;

export const Chips = z.number().int().nonnegative();
export const SeatIndex = z.number().int().min(0).max(9);

/**
 * Credentials, bounded at the edge.
 *
 * The lengths are not security limits — they stop one client making the server
 * hash a megabyte, and they are the same limits the account layer enforces, so
 * a value that parses here cannot be rejected there for its shape alone.
 */
export const SessionToken = z.string().min(32).max(128);
/** A one-time link token. Same shape as a session, different lifetime. */
export const LinkToken = z.string().min(32).max(128);
export const Email = z.string().trim().min(3).max(254).email();
/**
 * What somebody types to sign in.
 *
 * A display name or an email address. Registering still demands a real address
 * — that is the one the recovery link goes to — but making people remember
 * which of the two the room wanted is a wall for no benefit, and it is the wall
 * the room's own owner walked into first.
 */
export const Identifier = z.string().trim().min(3).max(254);
export const Password = z.string().min(8).max(200);
export const DisplayName = z.string().trim().min(3).max(24);

export const Street = z.enum(['preflop', 'flop', 'turn', 'river', 'complete']);
export type Street = z.infer<typeof Street>;

export const SeatStatus = z.enum(['active', 'folded', 'all-in', 'sitting-out', 'empty']);
export type SeatStatus = z.infer<typeof SeatStatus>;

// ---------------------------------------------------------------------------
// Client -> server
// ---------------------------------------------------------------------------

/**
 * Bet and raise carry a `to` — the seat's total for the street after the
 * action — rather than an increment. Clients say "raise to 300"; totals have
 * exactly one reading, where an increment has to be interpreted against a
 * street total the client may have stale.
 */
export const PlayerAction = z.discriminatedUnion('type', [
  z.object({ type: z.literal('fold') }),
  z.object({ type: z.literal('check') }),
  z.object({ type: z.literal('call') }),
  z.object({ type: z.literal('bet'), to: Chips }),
  z.object({ type: z.literal('raise'), to: Chips }),
]);
export type PlayerAction = z.infer<typeof PlayerAction>;

export const ClientMessage = z.discriminatedUnion('type', [
  /**
   * Open the connection with an existing session.
   *
   * The session token replaces the shared access code entirely. A seat is now
   * reclaimed because the account is known, not because the client remembered
   * an opaque id — which also means a reconnect gets the same chips back
   * rather than a new identity.
   */
  z.object({
    type: z.literal('hello'),
    protocol: z.number().int(),
    session: SessionToken,
    /** Optional contribution to the shuffle. Never required. */
    clientSeed: z.string().max(64).optional(),
  }),
  z.object({
    type: z.literal('register'),
    protocol: z.number().int(),
    email: Email,
    password: Password,
    displayName: DisplayName,
  }),
  z.object({
    type: z.literal('login'),
    protocol: z.number().int(),
    /** A display name or an email address; the server accepts either. */
    email: Identifier,
    password: Password,
  }),
  z.object({ type: z.literal('logout') }),
  /**
   * The three messages that work without a session.
   *
   * A player who cannot log in is exactly the player who needs these, so they
   * are answered on an anonymous connection. They carry a link's token rather
   * than an identity — the token is the proof.
   */
  z.object({ type: z.literal('verify-email'), token: LinkToken }),
  z.object({ type: z.literal('request-reset'), email: Email }),
  z.object({ type: z.literal('reset-password'), token: LinkToken, password: Password }),
  /** Send the verification link again. Needs a session; the address is on file. */
  z.object({ type: z.literal('resend-verification') }),
  /** Take the once-a-day top-up. Idempotent; asking twice is not an error. */
  z.object({ type: z.literal('claim-daily') }),
  z.object({ type: z.literal('watch'), tableId: z.string().min(1).max(64) }),
  // Stop watching. Without it, "back to the lobby" only stopped the client
  // replaying the table on reconnect — the connection stayed subscribed, the
  // next snapshot put the player straight back at it, and the button looked
  // broken because from the outside it was.
  z.object({ type: z.literal('unwatch') }),
  /**
   * Sit & Go.
   *
   * Registering is the only way into a tournament — there is no 'sit', because
   * seats are drawn rather than chosen, and the buy-in is fixed. Unregistering
   * is allowed right up until the game starts, and refunds in full.
   */
  z.object({ type: z.literal('register-sng'), tournamentId: z.string().min(1).max(64) }),
  z.object({ type: z.literal('unregister-sng'), tournamentId: z.string().min(1).max(64) }),
  z.object({ type: z.literal('sit'), seat: SeatIndex, buyIn: Chips }),
  z.object({ type: z.literal('stand') }),
  /** Top a seat back up. Going broke should not end the session. */
  z.object({ type: z.literal('rebuy'), amount: Chips }),
  z.object({ type: z.literal('sit-out'), out: z.boolean() }),
  z.object({
    type: z.literal('act'),
    /** Which hand this decision was made for. A stale one is dropped, not applied. */
    handNumber: z.number().int().nonnegative(),
    action: PlayerAction,
  }),
  z.object({ type: z.literal('ping'), at: z.number().int() }),
]);
export type ClientMessage = z.infer<typeof ClientMessage>;

// ---------------------------------------------------------------------------
// Server -> client
// ---------------------------------------------------------------------------

export interface SeatView {
  readonly seat: number;
  readonly name: string | null;
  readonly stack: number;
  readonly streetCommitted: number;
  readonly status: SeatStatus;
  /** Present only for the recipient's own seat, and for everyone at showdown. */
  readonly cards: readonly CardCode[] | null;
  /** How many hole cards this seat holds, when the cards themselves are hidden. */
  readonly cardCount: number;
  /**
   * Sitting out from the next hand.
   *
   * Distinct from `status`, which stays 'active' for a player who sat out
   * mid-hand: they finish the hand they are already in. Without this the
   * player clicks the button, sees nothing change, and clicks it again.
   */
  readonly sitOutNextHand: boolean;
  /**
   * Seconds this seat has banked for a hard decision.
   *
   * Public, unlike anything to do with cards. A time bank draining is a fact
   * about the table everyone is sitting at — it is why the game has stopped —
   * and hiding it would only make the pause look like a broken connection.
   */
  readonly timeBankMs: number;
  /**
   * A house bot, said out loud.
   *
   * Not a courtesy. A player has a right to know who they are playing against,
   * and a room that hides it once has to keep hiding it. The name carries the
   * label too, so a screenshot still tells the truth.
   */
  readonly isBot: boolean;
  readonly isYou: boolean;
}

export interface LegalActionsView {
  readonly fold: boolean;
  readonly check: boolean;
  readonly call: { readonly amount: number; readonly allIn: boolean } | null;
  readonly bet: { readonly min: number; readonly max: number } | null;
  readonly raise: { readonly min: number; readonly max: number } | null;
}

/**
 * What just happened, kept alive between hands.
 *
 * Without this a showdown lasts exactly one frame: the engine finishes the
 * hand, the runner drops its state, and the next snapshot has no cards in it
 * at all. Players saw the pot move and never learned why. The result rides on
 * the snapshot rather than only on an event so that a reconnect, a slow
 * network and a fresh tab all still see it.
 */
export interface HandResultView {
  readonly handNumber: number;
  /** Cards that were actually shown down. Empty when everyone folded. */
  readonly shown: readonly {
    readonly seat: number;
    readonly cards: readonly CardCode[];
    /** Stable category id from the engine, e.g. 'two-pair'. */
    readonly category: string;
    /** The five cards that actually played, for highlighting. */
    readonly best: readonly CardCode[];
  }[];
  readonly awards: readonly { readonly seat: number; readonly amount: number }[];
  /** Chips won or lost over the hand, per seat that was in it. */
  readonly net: readonly { readonly seat: number; readonly amount: number }[];
}

export interface PotView {
  readonly amount: number;
  readonly eligible: readonly number[];
}

/**
 * Everything the recipient may see, in one message.
 *
 * A snapshot rather than a diff on join and on every hand start: reconnect,
 * a late watcher, and a fresh page load are then the same code path, and
 * there is no way for a client to drift out of sync and stay there.
 */
export interface TableSnapshot {
  readonly tableId: string;
  readonly name: string;
  readonly smallBlind: number;
  readonly bigBlind: number;
  /** The big-blind ante for the next hand, or zero. */
  readonly ante: number;
  readonly maxSeats: number;
  readonly handNumber: number;
  readonly street: Street;
  readonly board: readonly CardCode[];
  readonly pots: readonly PotView[];
  readonly potTotal: number;
  readonly buttonSeat: number | null;
  readonly actionOn: number | null;
  /** Milliseconds left on the acting seat's clock, or null when nobody is to act. */
  readonly actionDeadlineMs: number | null;
  /** True once the clock showing is the actor’s own banked time, not the free one. */
  readonly onTimeBank: boolean;
  readonly seats: readonly SeatView[];
  /** Your seat, or null if you are watching. */
  readonly yourSeat: number | null;
  /** Populated only when it is your turn. */
  readonly legalActions: LegalActionsView | null;
  /** Published before the deal; the seed that matches it arrives when the hand ends. */
  readonly commitment: string | null;
  /** Set only in the pause between hands, so the result can be read. */
  readonly result: HandResultView | null;
  /**
   * Why no hand is running, when none is.
   *
   * A table that simply stops is indistinguishable from a broken one. Saying
   * which it is costs nothing and is the difference between waiting and
   * refreshing.
   */
  readonly waitingFor: 'players' | 'next-hand' | 'paused' | null;
  readonly minBuyIn: number;
  readonly maxBuyIn: number;
  /** Null at a cash table. */
  readonly tournament: TournamentTableView | null;
}

/** A redacted hand event. The shape mirrors the engine's, with cards as codes. */
export type ServerHandEvent =
  | { readonly type: 'hand-started'; readonly handNumber: number; readonly buttonSeat: number; readonly commitment: string }
  | { readonly type: 'blind-posted'; readonly seat: number; readonly amount: number; readonly blind: 'small' | 'big'; readonly allIn: boolean }
  /** A big-blind ante: dead money, matched by nobody, owed back to nobody. */
  | { readonly type: 'ante-posted'; readonly seat: number; readonly amount: number; readonly allIn: boolean }
  | { readonly type: 'hole-cards-dealt'; readonly seat: number; readonly cards: readonly CardCode[] | null; readonly cardCount: number }
  | { readonly type: 'acted'; readonly seat: number; readonly action: PlayerAction['type']; readonly amount: number; readonly to: number; readonly allIn: boolean; readonly byTimeout: boolean }
  | { readonly type: 'street-dealt'; readonly street: Street; readonly cards: readonly CardCode[] }
  | { readonly type: 'uncalled-returned'; readonly seat: number; readonly amount: number }
  | { readonly type: 'pots-built'; readonly pots: readonly PotView[] }
  /** The house took its cut. Public: a player is entitled to see what it cost. */
  | { readonly type: 'raked'; readonly total: number }
  | { readonly type: 'showdown'; readonly shown: readonly { readonly seat: number; readonly cards: readonly CardCode[] }[] }
  | { readonly type: 'awarded'; readonly seat: number; readonly amount: number; readonly potIndex: number }
  | { readonly type: 'hand-complete'; readonly handNumber: number };

/** Who the connection belongs to, and what they hold away from any table. */
export interface AccountView {
  readonly accountId: string;
  readonly displayName: string;
  readonly email: string;
  /** Chips in the wallet — not the chips in front of a seat. */
  readonly balance: number;
  /** Whether today's grant is still there to take. */
  readonly dailyGrantAvailable: boolean;
  readonly emailVerified: boolean;
}

/** What a notice is about. The text is Persian and comes from the server. */
export const NOTICE_CODES = [
  'registered',
  'unregistered',
  'verification-sent',
  'email-verified',
  'reset-requested',
  'password-changed',
] as const;
export type NoticeCode = (typeof NOTICE_CODES)[number];

export type ServerMessage =
  | {
      readonly type: 'welcome';
      readonly protocol: number;
      readonly account: AccountView;
      readonly tables: readonly TableListing[];
      readonly tournaments: readonly TournamentListing[];
    }
  /** A fresh session after register or login. The client stores this. */
  | { readonly type: 'session'; readonly token: string; readonly account: AccountView }
  /** The wallet moved — a buy-in, a cash-out, a grant. */
  | { readonly type: 'balance'; readonly balance: number; readonly dailyGrantAvailable: boolean }
  /** The Sit & Go list changed: someone registered, a game started or finished. */
  | { readonly type: 'tournaments'; readonly tournaments: readonly TournamentListing[] }
  | { readonly type: 'snapshot'; readonly table: TableSnapshot }
  | { readonly type: 'event'; readonly tableId: string; readonly seq: number; readonly event: ServerHandEvent }
  | { readonly type: 'seat-update'; readonly tableId: string; readonly seats: readonly SeatView[] }
  /**
   * Something worked, and the player should be told.
   *
   * Separate from 'error' because the client styles them differently, and
   * separate from a bare boolean reply because some of these arrive on an
   * anonymous connection with no request to answer.
   */
  | { readonly type: 'notice'; readonly code: NoticeCode; readonly message: string }
  | { readonly type: 'error'; readonly code: ErrorCode; readonly message: string }
  | { readonly type: 'pong'; readonly at: number };

/** What a Sit & Go is doing, for the lobby. */
export type TournamentStatus = 'registering' | 'running' | 'finished' | 'cancelled';

export interface TournamentListing {
  readonly tournamentId: string;
  readonly name: string;
  readonly status: TournamentStatus;
  readonly speed: 'regular' | 'turbo';
  /** In wallet chips. Every entrant pays the same. */
  readonly buyIn: number;
  /** How many can enter. More than one table's worth makes it an MTT. */
  readonly seats: number;
  /** Seats at each table. Equal to `seats` for a Sit & Go. */
  readonly seatsPerTable: number;
  readonly registered: number;
  /** Tournament chips each entrant starts with. Not money. */
  readonly startingStack: number;
  readonly prizePool: number;
  /** What each paid place wins, first to last. */
  readonly payouts: readonly number[];
  /** True when this connection is registered. */
  readonly youAreIn: boolean;
  /** Set once the game is running, so the client can open the table. */
  readonly tableId: string | null;
}

/**
 * The tournament state that belongs on the table itself.
 *
 * Rides on the snapshot rather than arriving as its own message, for the same
 * reason the hand result does: a player reading the screen needs the level, the
 * clock and how many are left in the same frame as the stacks they are
 * comparing them against.
 */
export interface TournamentTableView {
  readonly tournamentId: string;
  readonly level: number;
  /** The big-blind ante at this level, or zero on the early ones. */
  readonly ante: number;
  /** Null once the ladder is capped and the blinds stop rising. */
  readonly nextLevelInMs: number | null;
  readonly entrants: number;
  readonly remaining: number;
  /** How many tables are still running. One, for a Sit & Go. */
  readonly tables: number;
  /**
   * Every table is waiting for the others between hands.
   *
   * Only on the bubble, and only with more than one table. Said out loud
   * because otherwise the game appears to stop for no reason at the tensest
   * moment of the tournament.
   */
  readonly handForHand: boolean;
  readonly prizePool: number;
  readonly payouts: readonly number[];
  /** Filled in as players bust, best first. */
  readonly finished: readonly { readonly place: number; readonly name: string; readonly prize: number }[];
}

export interface TableListing {
  readonly tableId: string;
  readonly name: string;
  readonly smallBlind: number;
  readonly bigBlind: number;
  readonly maxSeats: number;
  readonly seated: number;
}

export const ERROR_CODES = [
  'bad-message',
  'bad-protocol',
  // The session is dead: the token no longer names anybody, and no amount of
  // retrying will change that. The only cure is credentials.
  'unauthorized',
  // This connection has not said who it is yet. Nothing is wrong with the
  // session; the message simply arrived before the handshake finished. These
  // were one code until a restart threw away every logged-in player's token
  // over a message that was merely early.
  'not-authenticated',
  'not-found',
  'seat-taken',
  'already-seated',
  'not-seated',
  'bad-buy-in',
  'cannot-rebuy',
  'cannot-register',
  'invalid-credentials',
  'name-taken',
  'email-taken',
  'invalid-input',
  'bad-token',
  'insufficient-funds',
  'not-your-turn',
  'illegal-action',
  'stale-hand',
  'rate-limited',
  'internal',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/**
 * Parse an incoming frame.
 *
 * Returns a result rather than throwing: a malformed frame is an ordinary event
 * on a public socket, not an exception. The caller replies with an error and
 * keeps the connection, or closes it — its choice, not a stack unwind's.
 */
export function parseClientMessage(raw: string): { ok: true; message: ClientMessage } | { ok: false; reason: string } {
  if (raw.length > 8192) return { ok: false, reason: 'frame too large' };
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, reason: 'not valid JSON' };
  }
  const parsed = ClientMessage.safeParse(json);
  if (!parsed.success) {
    return { ok: false, reason: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
  }
  return { ok: true, message: parsed.data };
}

export function encode(message: ServerMessage): string {
  return JSON.stringify(message);
}
